import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal, ListPlus, Plus, Share2 } from 'lucide-react'
import { usePlayerStore } from '../stores/player'
import AddToPlaylistModal from './AddToPlaylistModal'
import ShareTrackModal from './ShareTrackModal'
import type { Track, TrackWithDetails } from '../types'

interface TrackMenuProps {
  track: Track | TrackWithDetails
}

export default function TrackMenu({ track }: TrackMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [showPlaylistModal, setShowPlaylistModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, openUpward: false })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const { addToQueue, queue } = usePlayerStore()

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false)
    }

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const menuHeight = 160 // Approximate height of the menu
      const menuWidth = 192 // w-48 = 12rem = 192px
      const viewportHeight = window.innerHeight
      const playerBarHeight = 80 // Height of the player bar at the bottom

      // Check if there's enough space below (accounting for player bar)
      const spaceBelow = viewportHeight - rect.bottom - playerBarHeight
      const openUpward = spaceBelow < menuHeight

      // Position menu aligned to the right of the button
      let left = rect.right - menuWidth
      // Ensure menu doesn't go off the left edge
      if (left < 8) left = 8

      setMenuPosition({
        top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
        left,
        openUpward
      })
    }

    setIsOpen(!isOpen)
  }

  const handleAddToQueue = (e: React.MouseEvent) => {
    e.stopPropagation()
    addToQueue(track)
    setIsOpen(false)
  }

  const handleAddToPlaylist = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowPlaylistModal(true)
    setIsOpen(false)
  }

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowShareModal(true)
    setIsOpen(false)
  }

  const menuContent = isOpen && createPortal(
    <div
      ref={menuRef}
      className="fixed w-48 bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-[100] py-1"
      style={{ top: menuPosition.top, left: menuPosition.left }}
    >
      <button
        onClick={handleAddToQueue}
        className="w-full px-4 py-2 text-left text-sm text-dark-200 hover:bg-dark-700 flex items-center gap-3"
      >
        <ListPlus size={16} />
        Add to Queue
        {queue.length > 0 && (
          <span className="ml-auto text-xs text-dark-400">
            ({queue.length} in queue)
          </span>
        )}
      </button>

      <div className="border-t border-dark-700 my-1" />

      <button
        onClick={handleAddToPlaylist}
        className="w-full px-4 py-2 text-left text-sm text-dark-200 hover:bg-dark-700 flex items-center gap-3"
      >
        <Plus size={16} />
        Add to Playlist
      </button>

      <div className="border-t border-dark-700 my-1" />

      <button
        onClick={handleShare}
        className="w-full px-4 py-2 text-left text-sm text-dark-200 hover:bg-dark-700 flex items-center gap-3"
      >
        <Share2 size={16} />
        Share
      </button>
    </div>,
    document.body
  )

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1 rounded hover:bg-dark-600 text-dark-400 hover:text-dark-200 transition-colors"
        title="More options"
      >
        <MoreHorizontal size={18} />
      </button>

      {menuContent}

      <AddToPlaylistModal
        track={track}
        isOpen={showPlaylistModal}
        onClose={() => setShowPlaylistModal(false)}
      />

      <ShareTrackModal
        track={track}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  )
}
